import {useMemo} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import PropTypes from 'prop-types';

import {useCategory, useProductSearch} from '@salesforce/commerce-sdk-react';
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks';

import {ProductList as ProductListComponent} from '@amblique/king-living/app/components/product-list';
import useLazyProductSearch from '@amblique/king-living/app/hooks/use-lazy-product-search';
import {useBodyPageType} from '@amblique/king-living/app/hooks/use-body-page-type';
import {RANGE_PLP_PAGE_SIZE} from '@amblique/king-living/app/constants';

// King Living: Pixlee integration
import {usePixleeScript} from '@amblique/king-living/app/hooks/use-pixlee-script';

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
const ProductList = (props) => {
  const {categoryId: categoryIdProp, ...restProps} = props;
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlParams = new URLSearchParams(location.search);
  const searchPhrase = urlParams.get('q');
  const isSearch = !!searchPhrase;

  // When rendered from DynamicPage (slug → category fallback), categoryId is passed as prop
  const categoryId = categoryIdProp ?? params.categoryId;

  useBodyPageType(categoryId || isSearch ? 'category-listing-page' : null);

  if (categoryId) {
    searchParams._refine.push(`cgid=${categoryId}`);
  }

  const {_refine, ...restOfParams} = searchParams;

  // King Living: Load Pixlee script for UGC widgets on PLP
  usePixleeScript();

  // `/search?q=…`: infinite scroll batches (same as Range PLP). Category routes keep URL pagination.
  const lazySearch = useLazyProductSearch({
    searchPhrase: isSearch ? searchPhrase : undefined,
    sort: restOfParams.sort,
    urlRefine: _refine,
    pageSize: RANGE_PLP_PAGE_SIZE,
  });

  const {
    isLoading: standardLoading,
    isFetched: standardFetched,
    isRefetching: standardRefetching,
    data: standardProductSearchResult,
  } = useProductSearch(
    {
      parameters: {
        ...restOfParams,
        perPricebook: true,
        allVariationProperties: true,
        allImages: true,
        expand: [
          'promotions',
          'variations',
          'prices',
          'images',
          'page_meta_tags',
          'custom_properties'
        ],
        refine: _refine,
      },
    },
    {
      keepPreviousData: true,
      enabled: !isSearch,
    }
  );

  const productSearchResult = useMemo(() => {
    if (!isSearch) {
      return standardProductSearchResult;
    }
    const pr = lazySearch.pageResult;

    if (!pr) {
      return undefined;
    }

    return {
      ...pr,
      hits: lazySearch.hits,
    };
  }, [isSearch, standardProductSearchResult, lazySearch.pageResult, lazySearch.hits]);

  const isLoading = isSearch ? lazySearch.isInitialLoading : standardLoading;
  const isFetched = isSearch ?
    Boolean(lazySearch.pageResult || lazySearch.hits.length > 0) :
    standardFetched;
  const isRefetching = isSearch ?
    lazySearch.isLoadingMore || lazySearch.isRefining :
    standardRefetching;

  const {error, data: category} = useCategory(
    {
      parameters: {
        id: categoryId,
      },
    },
    {
      enabled: !isSearch && !!categoryId,
    }
  );

  const searchLazyPagination = isSearch ?
    {
      loadMore: lazySearch.loadMore,
      hasMore: lazySearch.hasMore,
      isLoadingMore: lazySearch.isLoadingMore,
      pageSize: RANGE_PLP_PAGE_SIZE,
    } :
    undefined;

  return (
    <ProductListComponent
      category={category}
      categoryId={categoryId}
      error={error}
      isFetched={isFetched}
      isLoading={isLoading}
      isRefetching={isRefetching}
      productSearchResult={productSearchResult}
      searchLazyPagination={searchLazyPagination}
      {...restProps}
    />
  );
};

ProductList.getTemplateName = () => 'product-list';

ProductList.propTypes = {
  categoryId: PropTypes.string,
  onAddToWishlistClick: PropTypes.func,
  onRemoveWishlistClick: PropTypes.func,
  category: PropTypes.object,
};

export default ProductList;
